import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import Select from 'react-select';
import { Squad, User } from '../../../types';
import '../styles/ScheduleEventModal.css';
import { createChicagoDate } from '../../../shared/utils/timezone';

interface ScheduleEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (eventData: any) => void;
  squads: Squad[];
  teachers: User[];
  selectedDate: Date | null;
}

const ScheduleEventModal: React.FC<ScheduleEventModalProps> = ({ isOpen, onClose, onSave, squads, teachers, selectedDate }) => {
  const [title, setTitle] = useState('');
  const [squadId, setSquadId] = useState('');
  const [teacherId, setTeacherId] = useState('');
  const [supportingTeacherIds, setSupportingTeacherIds] = useState<string[]>([]);
  const [startTime, setStartTime] = useState('15:30');
  const [endTime, setEndTime] = useState('16:30');
  const [eventDate, setEventDate] = useState(selectedDate ? selectedDate.toISOString().split('T')[0] : '');
  const [isRecurring, setIsRecurring] = useState(false);
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>([]);
  const [seriesStart, setSeriesStart] = useState('');
  const [seriesEnd, setSeriesEnd] = useState('');

  if (!isOpen) return null;

  const handleSupportingTeacherChange = (teacherId: string) => {
    setSupportingTeacherIds(prev =>
      prev.includes(teacherId)
        ? prev.filter(id => id !== teacherId)
        : [...prev, teacherId]
    );
  };

  const handleDayToggle = (day: number) => {
    setDaysOfWeek(prev => 
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  };

  const handleSave = async () => {
    if (!squadId) {
      void window.appAlert('Please select a squad.');
      return;
    }

    const selectedSquad = squads.find(s => s.id === squadId);
    
    if (isRecurring) {
      if (daysOfWeek.length === 0 || !seriesEnd) {
        void window.appAlert('Please select repeat days and an end date for the series.');
        return;
      }
      
      onSave({
        isRecurring: true,
        squadId,
        title: title || selectedSquad?.title,
        mainTeacherId: teacherId,
        supportingTeacherIds,
        startTime,
        endTime,
        seriesStart: seriesStart || selectedDate?.toISOString().split('T')[0],
        seriesEnd,
        daysOfWeek,
      });
      
          } else {
        if (!eventDate) {
          void window.appAlert('Please select a date for the event.');
          return;
        }

        // Create dates in Chicago timezone
        const startDate = createChicagoDate(eventDate, startTime);
        const endDate = createChicagoDate(eventDate, endTime);

        onSave({
          isRecurring: false,
          squadId,
          title: title || selectedSquad?.title,
          mainTeacherId: teacherId,
          supportingTeacherIds,
          start: startDate,
          end: endDate,
        });
      }
  };
  
  return (
    <div className="schedule-modal-overlay" onClick={onClose}>
      <div className="schedule-modal" onClick={e => e.stopPropagation()}>
        <h2>Schedule New Class</h2>
        <div className="modal-timezone-indicator">
          📍 Enter times in Central Time (CT)
        </div>
        <div className="form-group">
          <label htmlFor="squad-select">Squad</label>
          <select id="squad-select" value={squadId} onChange={e => setSquadId(e.target.value)}>
            <option value="" disabled>Select a squad</option>
            {squads.map(squad => (
              <option key={squad.id} value={squad.id}>{squad.title}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="teacher-select">Lead Teacher (optional)</label>
          <select id="teacher-select" value={teacherId} onChange={e => setTeacherId(e.target.value)}>
            <option value="">None</option>
            {teachers.map(teacher => (
              <option key={teacher.id} value={teacher.id}>{teacher.displayName}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="supporting-teacher-select">Supporting Teachers (optional)</label>
          <Select
            id="supporting-teacher-select"
            isMulti
            options={[...teachers]
              .sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''))
              .map(teacher => ({ value: teacher.id, label: teacher.displayName || '' }))
            }
            value={teachers
              .filter(teacher => supportingTeacherIds.includes(teacher.id))
              .map(teacher => ({ value: teacher.id, label: teacher.displayName || '' }))
            }
            onChange={(selectedOptions) => {
              setSupportingTeacherIds(selectedOptions.map(o => o.value));
            }}
            className="react-select-container"
            classNamePrefix="react-select"
            menuPortalTarget={document.body}
            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
          />
        </div>
        <div className="form-group">
          <label htmlFor="event-title">Event Title (optional)</label>
          <input
            id="event-title"
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Defaults to squad title"
          />
        </div>
        {!isRecurring && (
            <div className="form-group">
                <label htmlFor="event-date">Date</label>
                <input id="event-date" type="date" value={eventDate} onChange={e => setEventDate(e.target.value)} />
            </div>
        )}
        <div className="form-group-row">
          <div className="form-group">
            <label htmlFor="start-time">Start Time (CT)</label>
            <input id="start-time" type="time" value={startTime} onChange={e => setStartTime(e.target.value)} />
          </div>
          <div className="form-group">
            <label htmlFor="end-time">End Time (CT)</label>
            <input id="end-time" type="time" value={endTime} onChange={e => setEndTime(e.target.value)} />
          </div>
        </div>

        <div className="form-group">
          <label className="recurring-label">
            <input type="checkbox" checked={isRecurring} onChange={e => setIsRecurring(e.target.checked)} />
            Recurring Event
          </label>
        </div>

        {isRecurring && (
          <div className="recurring-options">
            <div className="form-group">
              <label htmlFor="series-start">Start Date</label>
              <input id="series-start" type="date" value={seriesStart} onChange={e => setSeriesStart(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Repeat on</label>
              <div className="day-picker">
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
                  <button 
                    key={index} 
                    className={`day-btn ${daysOfWeek.includes(index) ? 'selected' : ''}`}
                    onClick={() => handleDayToggle(index)}
                  >
                    {day}
                  </button>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="series-end">End Date</label>
              <input id="series-end" type="date" value={seriesEnd} onChange={e => setSeriesEnd(e.target.value)} />
            </div>
          </div>
        )}

        <div className="modal-actions">
          <button onClick={onClose}>Cancel</button>
          <button onClick={handleSave}>Save</button>
        </div>
      </div>
    </div>
  );
};

export default ScheduleEventModal;
